import { useQueryClient } from '@tanstack/react-query';
import {
  NonIntegratedSourcingChannelsResponse,
  UtmSourceMappingKeysResponse,
} from 'api-clients/monolith';
import { useEffect, useMemo, useState } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { useConfirmation } from '@/components/base/confirmation';
import { ChannelIcon } from '@/components/ui/channel-icon';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { FormSelect } from '@/components/ui/select';
import { Tag } from '@/components/ui/tag';
import { Typography } from '@/components/ui/typography';
import { queryKeys } from '@/query-keys';
export interface FormInput {
  channelId: string | null;
  isNewChannel: boolean;
  utmSourceIds: string[];
  channelName: string | null;
}

interface UtmMappingFormProps {
  hookForm: UseFormReturn<FormInput>;
  manageChannelId?: string;
  /** Called when user chooses to save before changing channel (e.g. from confirm dialog). */
  onRequestSubmit?: () => void;
}

export function UtmMappingForm({
  hookForm,
  manageChannelId,
  onRequestSubmit,
}: UtmMappingFormProps) {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const { confirm } = useConfirmation();
  const [utmSourceSearchQuery, setUtmSourceSearchQuery] = useState('');

  const {
    control,
    formState: { isDirty },
    getValues,
    resetField,
    setValue,
    watch,
    register,
    formState: { errors },
  } = hookForm;

  const selectedChannelId = watch('channelId');
  const selectedUtmSourceIds = watch('utmSourceIds');
  const isNewChannel = watch('isNewChannel');

  /* All channels from the query data retrieved from the ./non-integrated.tsx component */
  const allChannels = useMemo(
    () =>
      queryClient.getQueryData<NonIntegratedSourcingChannelsResponse>([
        ...queryKeys.settings.channels.nonIntegrated,
      ])?.sourcing_channels ?? [],
    [queryClient],
  );

  /* Channel options for the select */
  const channelOptions = useMemo(
    () =>
      allChannels.map(channel => ({
        label: (
          <div className="flex items-center gap-2">
            <ChannelIcon
              channelSlug={channel.channel_slug}
              channelName={channel.channel_name}
              variant="small"
              size="xs"
            />
            <Typography variant="subtitle1">{channel.channel_name}</Typography>
          </div>
        ),
        value: channel.channel_id,
      })),
    [allChannels],
  );

  /* All UTM sources from the query data retrieved from the ./unmapped-utms.tsx component */
  const allUtmSources = useMemo(
    () =>
      queryClient.getQueryData<UtmSourceMappingKeysResponse>([
        ...queryKeys.settings.channels.utmMapping,
      ])?.utm_source_mapping_keys ?? [],
    [queryClient],
  );

  /* UTM sources options for the select */
  const utmSourceOptions = useMemo(
    () =>
      allUtmSources
        // Filter out UTM sources that linked to other channels. The same UTM source cannot be linked to multiple channels.
        .filter(
          utmSource =>
            (!utmSource.channel_id ||
              utmSource.channel_id === selectedChannelId) &&
            utmSource.value
              .toLowerCase()
              .includes(utmSourceSearchQuery.toLowerCase()),
        )
        .map(utmSource => ({
          label: (
            <div className="flex flex-col">
              <Typography variant="body1">{utmSource.value}</Typography>
              <Typography variant="subtitle1" className="!text-fx-neutral-600">
                <FormattedMessage
                  defaultMessage="{count, plural, one {# applicant} other {# applicants}}"
                  description="Applicant count for UTM source"
                  values={{ count: utmSource.applicant_count }}
                />
              </Typography>
            </div>
          ),
          value: utmSource.mapping_key_id,
        })),
    [allUtmSources, selectedChannelId, utmSourceSearchQuery],
  );

  /* Map of UTM source IDs to their values for quick lookup */
  const utmSourceById = useMemo(
    () =>
      allUtmSources.reduce(
        (acc, utmSource) => ({
          ...acc,
          [utmSource.mapping_key_id]: {
            value: utmSource.value,
            applicantCount: utmSource.applicant_count,
          },
        }),
        {} as Record<string, { value: string; applicantCount: number }>,
      ),
    [allUtmSources],
  );

  /* Prefill the channel select with the selected channel */
  useEffect(() => {
    if (isNewChannel) return;

    if (!manageChannelId && channelOptions.length === 0) {
      setValue('isNewChannel', true, { shouldDirty: false });
      return;
    }

    resetField('channelId', {
      defaultValue: manageChannelId ?? channelOptions?.[0]?.value,
    });
  }, [channelOptions, resetField, setValue, manageChannelId, isNewChannel]);

  /* Sync UTM sources select with the selected channel whenever the selected channel changes */
  useEffect(() => {
    if (!selectedChannelId) return;

    resetField('utmSourceIds', {
      defaultValue:
        allChannels
          .find(channel => channel.channel_id === selectedChannelId)
          ?.utm_sources.map(utmSource => utmSource.mapping_key_id) ?? [],
    });
  }, [allChannels, selectedChannelId, resetField]);

  /* Check for unsaved changes before changing the channel */
  const handleChannelBeforeChange = async (selected: string[]) => {
    const newChannelId = selected[0] ?? null;

    // If the new channel ID is the same as the current channel ID, do nothing
    if (newChannelId === getValues('channelId')) return false;

    // If there are unsaved changes, show a confirmation dialog
    if (isDirty) {
      const saveFirst = await confirm({
        title: intl.formatMessage({
          defaultMessage: 'Unsaved changes',
          description: 'Title for alert',
        }),
        description: intl.formatMessage({
          defaultMessage:
            'Your changes will be lost if you change the channel. Please submit your changes first.',
          description: 'Message asking user to submit before changing channel',
        }),
        confirmLabel: intl.formatMessage({
          defaultMessage: 'Save changes',
          description: 'Submit button',
        }),
        cancelLabel: intl.formatMessage({
          defaultMessage: 'Clear',
          description: 'Cancel button',
        }),
      });

      // If the user confirms the save, submit the form
      if (saveFirst) {
        onRequestSubmit?.();
        return false;
      }
    }

    // Apply channel change and update its default so isDirty stays false
    resetField('channelId', { defaultValue: newChannelId });
    return false;
  };

  /* When the user toggles the custom channel checkbox, reset the form */
  const handleIsNewChannelChange = (value: boolean) => {
    setValue('isNewChannel', value, { shouldDirty: false });

    if (!value) {
      setValue('channelName', null, { shouldDirty: false });
      return;
    }

    setValue('channelId', null, { shouldDirty: false });
    setValue('utmSourceIds', [], { shouldDirty: false });
  };

  /* Remove a UTM source from the selected UTM sources */
  const handleRemoveUtmSource = (utmSourceId: string) => {
    setValue(
      'utmSourceIds',
      selectedUtmSourceIds.filter(id => id !== utmSourceId),
      { shouldDirty: true },
    );
  };

  return (
    <>
      <div className="flex flex-col gap-2">
        {/* Channel select */}
        <FormSelect
          control={control}
          name="channelId"
          label={intl.formatMessage({
            defaultMessage: 'Channel',
            description: 'Label for the channel select',
          })}
          placeholder={intl.formatMessage({
            defaultMessage: 'Select channel',
            description: 'Placeholder for the channel select',
          })}
          description={intl.formatMessage({
            defaultMessage:
              'Choose from existing channels or create a custom one',
            description: 'Description for the channel select',
          })}
          options={channelOptions}
          isMultiSelect={false}
          required
          isDisabled={isNewChannel}
          onBeforeChange={handleChannelBeforeChange}
          error={errors.channelId?.message}
        />

        {/* Custom channel checkbox */}
        <Controller
          control={control}
          name="isNewChannel"
          render={({ field }) => (
            <Checkbox
              checked={field.value}
              onCheckedChange={handleIsNewChannelChange}
              label={intl.formatMessage({
                defaultMessage: 'Create custom channel',
                description: 'Label for the custom channel checkbox',
              })}
            />
          )}
        />
      </div>

      {/* Custom channel name input */}
      {isNewChannel && (
        <Input
          {...register('channelName')}
          label={intl.formatMessage({
            defaultMessage: 'Name',
            description: 'Label for the channel name input',
          })}
          placeholder={intl.formatMessage({
            defaultMessage: 'Enter channel name',
            description: 'Placeholder for the channel name input',
          })}
          required
          error={errors.channelName?.message}
        />
      )}

      {/* UTM sources select */}
      <div className="flex flex-col gap-2">
        <FormSelect
          control={control}
          name="utmSourceIds"
          label={intl.formatMessage({
            defaultMessage: 'UTM sources',
            description: 'Label for the UTM sources select',
          })}
          placeholder={intl.formatMessage({
            defaultMessage: 'Select sources to map',
            description: 'Placeholder for the UTM sources select',
          })}
          options={utmSourceOptions}
          isMultiSelect
          required
          query={utmSourceSearchQuery}
          setQuery={q => setUtmSourceSearchQuery(q ?? '')}
          error={errors.utmSourceIds?.message}
        />

        <div className="flex flex-wrap gap-2">
          {selectedUtmSourceIds.map(utmSourceId => (
            <Tag
              key={utmSourceId}
              text={intl.formatMessage(
                {
                  defaultMessage: '{value} • {applicant_count} applicants',
                  description: 'UTM source value and applicant count',
                },
                {
                  value: utmSourceById[utmSourceId].value,
                  applicant_count: utmSourceById[utmSourceId].applicantCount,
                },
              )}
              className="bg-fx-neutral-100"
              onClose={() => handleRemoveUtmSource(utmSourceId)}
            />
          ))}
        </div>
      </div>
    </>
  );
}
